import React, { useEffect, useMemo, useState } from "react";
import AnalyticsOutlinedIcon from "@mui/icons-material/AnalyticsOutlined";
import ChatBubbleOutlineRoundedIcon from "@mui/icons-material/ChatBubbleOutlineRounded";
import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";
import axios from "axios";
import { useSelector } from "react-redux";
import { API_ENDPOINT } from "../../../services/api/resources";
import styles from "./NegotiationBot.module.css";
import NegotiationAnalysis from "./NegotiationAnalysis";
import NegotiationChat from "./NegotiationChat";
import NegotiationHistory from "./NegotiationHistory";
import RevisedOfferForm from "./RevisedOfferForm";
import {
  normalizeApiResponse,
  normalizeContextItems,
} from "./negotiationUtils";

const getAccessMode = (accessType, requestedMode) => {
  if (requestedMode === "vendor") return "vendor";
  const normalized = String(accessType || "").toLowerCase();
  return normalized.includes("vendor") ? "vendor" : "buyer";
};

const getHashQueryParam = (key) => {
  if (typeof window === "undefined") return "";
  return (
    new URLSearchParams(window.location.hash.split("?")[1] || "").get(key) || ""
  );
};

const getVendorToken = (loginData = {}) =>
  loginData?.vendor_token ||
  loginData?.token ||
  loginData?.user_token ||
  loginData?.vendor?.vendor_token ||
  loginData?.vendor?.token ||
  loginData?.user?.vendor_token ||
  loginData?.user?.user_token ||
  loginData?.user?.token ||
  "";

const getVendorContactToken = (loginData = {}) =>
  loginData?.vendor_contact_token ||
  loginData?.user?.vendor_contact_token ||
  "";

const getBuyerToken = (loginData = {}) =>
  loginData?.buyer_token ||
  loginData?.user_token ||
  loginData?.user?.buyer_token ||
  loginData?.user?.token ||
  "";

const getUniqueValues = (...values) =>
  values
    .map((value) => String(value || "").trim())
    .filter(Boolean)
    .filter((value, index, list) => list.indexOf(value) === index);

const mergeSessionMessages = (sessionData, messagesData) => {
  const sourceMessages =
    messagesData?.messages ||
    messagesData?.conversation ||
    messagesData?.results ||
    messagesData?.data ||
    [];

  return {
    ...(sessionData || {}),
    messages: Array.isArray(sourceMessages) ? sourceMessages : [],
  };
};

const BUYER_NAV_ITEMS = [
  { key: "analysis", label: "Analysis", Icon: AnalyticsOutlinedIcon },
  { key: "chat", label: "Chat", Icon: ChatBubbleOutlineRoundedIcon },
  { key: "history", label: "History", Icon: HistoryRoundedIcon },
];

const getVendorInboxPayloads = ({
  loginData = {},
  vendorBidItemToken = "",
  negotiationSessionToken = "",
}) => {
  const vendorToken = getVendorToken(loginData);
  const vendorContactToken = getVendorContactToken(loginData);
  const payloads = [];

  if (vendorToken) {
    payloads.push({ vendor_token: vendorToken });
  }

  if (vendorContactToken) {
    payloads.push({ vendor_contact_token: vendorContactToken });
    payloads.push({ vendor_token: vendorContactToken });
  }

  getUniqueValues(vendorBidItemToken).forEach((token) => {
    payloads.push({ vendor_bid_item_token: token });
  });

  getUniqueValues(negotiationSessionToken).forEach((token) => {
    payloads.push({ negotiation_session_token: token });
  });

  payloads.push({});

  return payloads.filter(
    (payload, index, list) =>
      list.findIndex((item) => JSON.stringify(item) === JSON.stringify(payload)) === index,
  );
};

const NegotiationBot = ({ context = {}, onClose }) => {
  const loginData = useSelector((state) => state.loginData.value);
  const authToken =
    loginData?.auth_token || loginData?.access_token || loginData?.token || "";
  const mode = getAccessMode(loginData?.access_type, context.mode);
  const contextItems = useMemo(
    () => normalizeContextItems(context.items || []),
    [context.items],
  );
  const [activeItemToken, setActiveItemToken] = useState(
    context.itemToken || contextItems[0]?.item_token || "",
  );
  const [history, setHistory] = useState(null);
  const [selectedVendorTokens, setSelectedVendorTokens] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [vendorSessions, setVendorSessions] = useState([]);
  const [selectedSessionToken, setSelectedSessionToken] = useState("");
  const [sessionDetail, setSessionDetail] = useState(null);
  const [vendorSubmitResponse, setVendorSubmitResponse] = useState(null);
  const [activeView, setActiveView] = useState(
    mode === "vendor" ? "vendor" : "analysis",
  );
  const [loading, setLoading] = useState(false);
  const [inboxLoading, setInboxLoading] = useState(false);
  const [error, setError] = useState("");

  const selectedItem = contextItems.find((item) => item.item_token === activeItemToken);
  const buyerContext = {
    rfqCode:
      context.rfqCode ||
      history?.rfq?.rfq_code ||
      "",
    rfqToken: context.rfqToken || "",
    itemToken: activeItemToken || "",
    items: contextItems,
  };
  const hiddenVendorSessionToken =
    context.negotiationSessionToken ||
    context.sessionToken ||
    getHashQueryParam("negotiation_session_token") ||
    "";
  const hiddenVendorBidItemToken =
    context.vendorBidItemToken ||
    context.vendor_bid_item_token ||
    getHashQueryParam("vendor_bid_item_token") ||
    "";

  const requestHeaders = useMemo(
    () => ({
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
    }),
    [authToken],
  );

  const loadVendorSessionDetail = async (sessionToken) => {
    if (!sessionToken) return;
    setLoading(true);
    setError("");

    const payload = {
      negotiation_session_token: sessionToken,
    };

    try {
      const response = await axios.post(
        API_ENDPOINT.negotiation_bot_vendor_session_detail,
        payload,
        requestHeaders,
      );
      const detailData = normalizeApiResponse(response);
      try {
        const messagesResponse = await axios.post(
          API_ENDPOINT.negotiation_bot_vendor_messages,
          payload,
          requestHeaders,
        );
        setSessionDetail(
          mergeSessionMessages(detailData, normalizeApiResponse(messagesResponse)),
        );
      } catch {
        setSessionDetail(detailData);
      }
      setSelectedSessionToken(sessionToken);
    } catch (detailError) {
      try {
        const fallbackResponse = await axios.post(
          API_ENDPOINT.negotiation_bot_vendor_messages,
          payload,
          requestHeaders,
        );
        setSessionDetail(normalizeApiResponse(fallbackResponse));
        setSelectedSessionToken(sessionToken);
      } catch (messagesError) {
        setError(
          messagesError?.response?.data?.message ||
            messagesError?.message ||
            detailError?.response?.data?.message ||
            detailError?.message ||
            "Unable to load vendor negotiation messages.",
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const loadVendorInbox = async () => {
    const payloads = getVendorInboxPayloads({
      loginData,
      vendorBidItemToken: hiddenVendorBidItemToken,
      negotiationSessionToken: hiddenVendorSessionToken,
    });

    setInboxLoading(true);
    setError("");
    try {
      let sessionList = [];

      for (const payload of payloads) {
        const response = await axios.post(
          API_ENDPOINT.vendor_negotiation_sessions,
          payload,
          requestHeaders,
        );
        const responseData = normalizeApiResponse(response);
        sessionList = Array.isArray(responseData)
          ? responseData
          : responseData.results || responseData.sessions || [];

        if (sessionList.length > 0) break;
      }

      setVendorSessions(sessionList);

      const firstSessionToken =
        hiddenVendorSessionToken ||
        sessionList[0]?.negotiation_session_token ||
        "";

      if (firstSessionToken) {
        loadVendorSessionDetail(firstSessionToken);
      }
    } catch (apiError) {
      setError(
        apiError?.response?.data?.message ||
          apiError?.message ||
          "Unable to load negotiation requests.",
      );
    } finally {
      setInboxLoading(false);
    }
  };

  const loadHistory = async (itemToken = activeItemToken) => {
    if (!buyerContext.rfqToken || !itemToken) {
      setError("Missing RFQ token or item token for negotiation history.");
      return;
    }

    setLoading(true);
    setError("");
    try {
      const response = await axios.post(
        API_ENDPOINT.negotiation_bot_history,
        {
          rfq_token: buyerContext.rfqToken,
          item_token: itemToken,
        },
        requestHeaders,
      );
      const responseData = normalizeApiResponse(response);
      setHistory(responseData);
      setSelectedVendorTokens(
        (responseData.current_quotations || [])
          .map((quote) => quote.vendor_bid_item_token)
          .filter(Boolean),
      );
    } catch (apiError) {
      setError(
        apiError?.response?.data?.message ||
          apiError?.message ||
          "Unable to load negotiation history.",
      );
    } finally {
      setLoading(false);
    }
  };

  const toggleVendor = (token) => {
    if (!token) return;
    setSelectedVendorTokens((prev) =>
      prev.includes(token)
        ? prev.filter((currentToken) => currentToken !== token)
        : [...prev, token],
    );
  };

  const toggleAllVendors = () => {
    const tokens = (history?.current_quotations || [])
      .map((quote) => quote.vendor_bid_item_token)
      .filter(Boolean);
    setSelectedVendorTokens((prev) =>
      prev.length === tokens.length ? [] : tokens,
    );
  };

  const sendRevisionRequest = async () => {
    const historyId = history?.previous_purchase?.history_id;
    if (!buyerContext.rfqToken || !activeItemToken || !historyId) {
      setError("Missing RFQ, item, or history id for revision request.");
      return;
    }

    setLoading(true);
    setError("");
    try {
      const response = await axios.post(
        API_ENDPOINT.negotiation_bot_request_revision,
        {
          rfq_token: buyerContext.rfqToken,
          item_token: activeItemToken,
          history_id: historyId,
          vendor_bid_item_tokens: selectedVendorTokens,
        },
        requestHeaders,
      );
      const responseData = normalizeApiResponse(response);
      const resultSessions = Array.isArray(responseData.results)
        ? responseData.results
        : [];
      const nextSelectedSessionToken =
        resultSessions[0]?.negotiation_session_token || "";
      setSessions(resultSessions);
      setSelectedSessionToken(nextSelectedSessionToken);
      setActiveView("chat");
    } catch (apiError) {
      setError(
        apiError?.response?.data?.message ||
          apiError?.message ||
          "Unable to send revision request.",
      );
    } finally {
      setLoading(false);
    }
  };

  const loadSessionDetail = async (sessionToken) => {
    if (!sessionToken) {
      setSessionDetail(null);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const response = await axios.post(
        API_ENDPOINT.negotiation_bot_session_detail,
        {
          rfq_token: buyerContext.rfqToken,
          negotiation_session_token: sessionToken,
        },
        requestHeaders,
      );
      const detailData = normalizeApiResponse(response);
      try {
        const messagesResponse = await axios.post(
          API_ENDPOINT.negotiation_bot_vendor_messages,
          {
            negotiation_session_token: sessionToken,
          },
          requestHeaders,
        );
        setSessionDetail(
          mergeSessionMessages(detailData, normalizeApiResponse(messagesResponse)),
        );
      } catch {
        setSessionDetail(detailData);
      }
      setSelectedSessionToken(sessionToken);
    } catch (apiError) {
      const detailMessage =
        apiError?.response?.data?.response ||
        apiError?.response?.data?.message ||
        apiError?.message ||
        "";
      if (String(detailMessage).toLowerCase().includes("session not found")) {
        setSelectedSessionToken("");
        setSessionDetail(null);
        setActiveView("analysis");
        setError("");
      } else {
        setError(detailMessage || "Unable to load session detail.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const loadBuyerSessions = async () => {
      if (!buyerContext.rfqToken) {
        if (activeItemToken) loadHistory(activeItemToken);
        return;
      }

      setInboxLoading(true);
      setError("");
      try {
        const response = await axios.post(
          API_ENDPOINT.buyer_negotiation_sessions,
          {
            rfq_token: buyerContext.rfqToken,
            ...(activeItemToken ? { item_token: activeItemToken } : {}),
          },
          requestHeaders,
        );
        const responseData = normalizeApiResponse(response);
        const sessionList = Array.isArray(responseData)
          ? responseData
          : responseData.results || responseData.sessions || [];

        if (sessionList.length > 0) {
          const nextSelectedSessionToken =
            selectedSessionToken ||
            sessionList[0]?.negotiation_session_token ||
            "";
          setSessions(sessionList);
          setSelectedSessionToken(nextSelectedSessionToken);
          setSessionDetail(sessionList[0] || null);
          setActiveView("chat");
          if (activeItemToken) {
            loadHistory(activeItemToken);
          }
          return;
        }

        if (activeItemToken) {
          setSessions([]);
          setSelectedSessionToken("");
          setSessionDetail(null);
          setActiveView("analysis");
          loadHistory(activeItemToken);
        }
      } catch (apiError) {
        if (activeItemToken) {
          setSessions([]);
          setSelectedSessionToken("");
          setSessionDetail(null);
          setActiveView("analysis");
          loadHistory(activeItemToken);
          return;
        }
        setError(
          apiError?.response?.data?.message ||
            apiError?.message ||
            "Unable to load buyer negotiation sessions.",
        );
      } finally {
        setInboxLoading(false);
      }
    };

    if (mode === "buyer") {
      loadBuyerSessions();
    }
  }, [activeItemToken, mode]);

  useEffect(() => {
    if (mode !== "buyer" || activeView !== "chat") return;
    if (!selectedSessionToken) {
      setSessionDetail(null);
      return;
    }
    loadSessionDetail(selectedSessionToken);
  }, [mode, activeView, selectedSessionToken]);

  useEffect(() => {
    if (mode === "vendor" && hiddenVendorSessionToken) {
      loadVendorSessionDetail(hiddenVendorSessionToken);
      return;
    }

    if (mode === "vendor") {
      loadVendorInbox();
    }
  }, [hiddenVendorSessionToken, mode]);

  const submitVendorOffer = async (payload) => {
    setLoading(true);
    setError("");
    try {
      const response = await axios.post(
        API_ENDPOINT.negotiation_bot_offer_submit,
        payload,
        requestHeaders,
      );
      const responseData = normalizeApiResponse(response);
      const nextSession = responseData.negotiation_session || responseData;
      const nextSessionToken =
        payload.negotiation_session_token ||
        nextSession?.negotiation_session_token ||
        selectedSessionToken ||
        "";

      setVendorSubmitResponse(nextSession);
      setSessionDetail(nextSession);
      setSelectedSessionToken(nextSessionToken);
      setVendorSessions((prevSessions) => {
        if (!nextSessionToken) return prevSessions;

        const hasSession = prevSessions.some(
          (session) => session.negotiation_session_token === nextSessionToken,
        );

        if (hasSession) {
          return prevSessions.map((session) =>
            session.negotiation_session_token === nextSessionToken
              ? { ...session, ...nextSession }
              : session,
          );
        }

        return [nextSession, ...prevSessions];
      });

      if (nextSessionToken) {
        await loadVendorSessionDetail(nextSessionToken);
      }

      return true;
    } catch (apiError) {
      setError(
        apiError?.response?.data?.message ||
          apiError?.message ||
          "Unable to submit revised offer.",
      );
      return false;
    } finally {
      setLoading(false);
    }
  };

  const sendBuyerChatMessage = async ({ negotiationSessionToken, message }) => {
    const trimmedMessage = String(message || "").trim();
    if (!negotiationSessionToken || !trimmedMessage) return false;

    setLoading(true);
    setError("");
    try {
      await axios.post(
        API_ENDPOINT.negotiation_bot_buyer_message,
        {
          rfq_token: buyerContext.rfqToken,
          negotiation_session_token: negotiationSessionToken,
          message: trimmedMessage,
          buyer_message: trimmedMessage,
          sender_role: "buyer",
          buyer_token: getBuyerToken(loginData),
        },
        requestHeaders,
      );
      await loadSessionDetail(negotiationSessionToken);
      return true;
    } catch (apiError) {
      setError(
        apiError?.response?.data?.message ||
          apiError?.response?.data?.response ||
          apiError?.message ||
          "Unable to send buyer message.",
      );
      return false;
    } finally {
      setLoading(false);
    }
  };

  const sendVendorChatMessage = async ({ negotiationSessionToken, message }) => {
    const trimmedMessage = String(message || "").trim();
    const targetSessionToken = negotiationSessionToken || selectedSessionToken;
    const activeVendorSession =
      vendorSessions.find(
        (session) => session.negotiation_session_token === targetSessionToken,
      ) ||
      vendorSessions[0] ||
      {};
    const vendorToken =
      getVendorToken(loginData) ||
      sessionDetail?.vendor_token ||
      activeVendorSession?.vendor_token ||
      "";
    const vendorRfqToken =
      context.rfqToken ||
      sessionDetail?.rfq_token ||
      sessionDetail?.rfq?.rfq_token ||
      activeVendorSession?.rfq_token ||
      activeVendorSession?.rfq?.rfq_token ||
      "";

    if (!vendorRfqToken || !targetSessionToken || !vendorToken) {
      setError("Missing RFQ token, negotiation session token, or vendor token.");
      return false;
    }
    if (!trimmedMessage) return false;

    setLoading(true);
    setError("");
    try {
      await axios.post(
        API_ENDPOINT.negotiation_bot_vendor_message,
        {
          rfq_token: vendorRfqToken,
          negotiation_session_token: targetSessionToken,
          message: trimmedMessage,
          vendor_message: trimmedMessage,
          sender_role: "vendor",
          vendor_token: vendorToken,
        },
        requestHeaders,
      );
      if (targetSessionToken) {
        await loadVendorSessionDetail(targetSessionToken);
      }
      return true;
    } catch (apiError) {
      setError(
        apiError?.response?.data?.message ||
          apiError?.response?.data?.response ||
          apiError?.message ||
          "Unable to send vendor message.",
      );
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleSelectSession = (sessionToken) => {
    setSelectedSessionToken(sessionToken);
    if (mode === "vendor") {
      loadVendorSessionDetail(sessionToken);
      return;
    }
  };

  const renderBuyerContent = () =>
    activeView === "analysis" ? (
      <NegotiationAnalysis
        history={history}
        selectedVendorTokens={selectedVendorTokens}
        onToggleVendor={toggleVendor}
        onToggleAll={toggleAllVendors}
        onSendRequest={sendRevisionRequest}
        loading={loading}
      />
    ) : activeView === "chat" ? (
      <NegotiationChat
        sessions={sessions}
        selectedSessionToken={selectedSessionToken}
        sessionDetail={sessionDetail}
        onSelectSession={handleSelectSession}
        onRefreshSession={loadSessionDetail}
        onSendMessage={sendBuyerChatMessage}
        loading={loading}
      />
    ) : (
      <NegotiationHistory sessionDetail={sessionDetail} />
    );

  return (
    <div
      className={`${styles.negotiationBot} ${
        mode === "buyer" ? styles.buyerBot : styles.vendorBot
      }`}
    >
      {/* <div className={styles.topBar}>
        <div>
          <span className={styles.eyebrow}>
            {mode === "vendor" ? "Vendor Negotiation Reply" : "RFQ Negotiation Bot"}
          </span>
          <h3>
            {mode === "vendor"
              ? context.rfqCode || "Negotiation"
              : "bidZ.ai Assistant"}
          </h3>
          <p>{selectedItem?.item_name || history?.item?.item_name || "RFQ conversation"}</p>
        </div>
        <button type="button" className={styles.iconBtn} onClick={onClose}>
          Close
        </button>
      </div> */}

      {mode === "buyer" && contextItems.length > 1 ? (
        <div className={styles.itemSelectRow}>
          {contextItems.map((item) => (
            <button
              type="button"
              key={item.item_token}
              className={item.item_token === activeItemToken ? styles.sessionActive : ""}
              onClick={() => setActiveItemToken(item.item_token)}
            >
              {item.item_name}
            </button>
          ))}
        </div>
      ) : null}

      {error ? <div className={styles.errorBox}>{error}</div> : null}

      {mode === "vendor" ? (
        <RevisedOfferForm
          sessionToken={hiddenVendorSessionToken || selectedSessionToken}
          vendorBidItemToken={hiddenVendorBidItemToken}
          rfqCode={context.rfqCode}
          itemName={context.itemName || selectedItem?.item_name}
          currentQuotedPrice={context.currentQuotedPrice}
          loading={loading}
          inboxLoading={inboxLoading}
          latestSession={vendorSubmitResponse}
          sessionDetail={sessionDetail}
          vendorSessions={vendorSessions}
          selectedSessionToken={selectedSessionToken}
          onSelectSession={handleSelectSession}
          onSendMessage={sendVendorChatMessage}
          onSubmit={submitVendorOffer}
        />
      ) : (
        <>
          <div className={styles.buyerViewScroller}>{renderBuyerContent()}</div>
          <div className={`${styles.tabRow} ${styles.buyerBottomNav}`}>
            {BUYER_NAV_ITEMS.map(({ key, label, Icon }) => (
              <button
                type="button"
                key={key}
                className={activeView === key ? styles.tabActive : ""}
                onClick={() => setActiveView(key)}
              >
                <Icon aria-hidden="true" className={styles.buyerNavIcon} />
                <span>{label}</span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default NegotiationBot;
